import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo
} from 'react';
import { useAuthContext } from '../AuthContext';
import { useErrorMessage } from '../../utils/errorMessage';

const OwnerContext = createContext();

/**
 * Provides the OwnerContext to its children components.
 * @context OwnerContext
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components.
 * @returns {JSX.Element} The OwnerContextProvider component.
 */
export const OwnerContextProvider = ({ children }) => {
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();

  const [isLoading, setIsLoading] = useState(false);
  const [owner, setOwner] = useState({});
  const [ownerSupervisions, setOwnerSupervisions] = useState([]);
  const [reviewActiveKey, setReviewActiveKey] = useState('received_reviews');
  const [questionnaireType, setQuestionnaireType] = useState('users:OWNER');
  const [veterinarian, setVeterinarian] = useState();
  const [groomer, setGroomer] = useState();

  /**
   * Retrieves the owner data from the API.
   * @param {string} id - The owner ID.
   * @returns {Promise<void>} A promise that resolves when the owner data is retrieved.
   */
  const getOwner = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/owners/${id}?populate=petsitting_address,animals,history.by,user`
      });

      setOwner(data);
    } catch (e) {
      message(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getOwnerSupervisions = useCallback(async (id) => {
    try {
      setIsLoading(true);
      const { data } = await dispatchAPI('GET', {
        url: `/supervisions?status=FINISHED`,
        body: {},
        params: {
          fields: 'reference,pensioners,main_informations',
          populate: 'pensioners,main_informations.owner',
          'main_informations.owner': id
        }
      });
      setOwnerSupervisions(data);
    } catch (e) {
      message(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getVeterinarian = async () => {
    try {
      const { data: veterinarianData } = await dispatchAPI('GET', {
        url: `/veterinarians/details/${owner?._id}`
      });
      if (veterinarianData) setVeterinarian(veterinarianData[0]);
    } catch (error) {
      message(error);
    }
  };

  const getGroomer = async () => {
    try {
      const { data: groomerData } = await dispatchAPI('GET', {
        url: `/groomers/details/${owner?._id}`
      });
      if (groomerData) setGroomer(groomerData[0]);
    } catch (error) {
      message(error);
    }
  };

  const handleChangeQuestionnaireType = useCallback((key) => {
    setReviewActiveKey(key);
    if (key === 'received_reviews') {
      setQuestionnaireType('users:PENSIONER');
    }
    if (key === 'left_reviews') {
      setQuestionnaireType('users:OWNER');
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (owner?._id) {
        await Promise.allSettled([getVeterinarian(), getGroomer()]);
      }
    })();
  }, [owner]);

  const contextValue = useMemo(
    () => ({
      owner,
      getOwner,
      ownerSupervisions,
      getOwnerSupervisions,
      isLoading,
      reviewActiveKey,
      setReviewActiveKey,
      veterinarian,
      setVeterinarian,
      groomer,
      setGroomer,
      handleChangeQuestionnaireType,
      questionnaireType,
      setQuestionnaireType,
      getVeterinarian,
      getGroomer
    }),
    [
      owner,
      getOwner,
      ownerSupervisions,
      getOwnerSupervisions,
      isLoading,
      reviewActiveKey,
      setReviewActiveKey,
      veterinarian,
      setVeterinarian,
      groomer,
      setGroomer,
      handleChangeQuestionnaireType,
      questionnaireType,
      setQuestionnaireType,
      getVeterinarian,
      getGroomer
    ]
  );

  return (
    <OwnerContext.Provider value={contextValue}>
      {children}
    </OwnerContext.Provider>
  );
};

export const useOwnerContext = () => useContext(OwnerContext);
